import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Eye, Hash, Loader2, PawPrint, Search, X } from "lucide-react";
import { api, errText, photoUrl } from "../lib/api";
import { useLanguage } from "../context/LanguageContext";
import { LostFoundId } from "./LostFoundId";

const TX = {
  en: { h: "Look up a Lost & Found ID", d: "Shelters, vets and neighbours: enter the ID printed on the photo or poster.", ph: "e.g. ON-100245", go: "Look up",
    lost: "Lost", found: "Found", home: "Reunited", seen: "Last seen", posted: "Posted", sightings: "sightings", open: "Open the ad" },
  fr: { h: "Rechercher un ID Perdu & Trouvé", d: "Refuges, vétérinaires et voisins : entrez l'ID imprimé sur la photo ou l'affiche.", ph: "ex. ON-100245", go: "Rechercher",
    lost: "Perdu", found: "Trouvé", home: "Réuni", seen: "Vu la dernière fois", posted: "Publié", sightings: "signalements", open: "Ouvrir l'annonce" },
};

const Preview = ({ l, x, lang, onClose }) => {
  const type = l.reunited_at ? "home" : l.lf_type === "found" ? "found" : "lost";
  const color = { lost: "bg-red-600", found: "bg-emerald-600", home: "bg-teal-500" }[type];
  const date = (l.lf_date || l.created_at || "").slice(0, 10);
  return (
    <div className="mt-4 flex gap-4 rounded-xl border border-slate-200 bg-white p-3 relative animate-in fade-in slide-in-from-top-1" data-testid="lf-lookup-result">
      <button type="button" onClick={onClose} className="absolute top-2 right-2 text-slate-400 hover:text-slate-600" aria-label="Close" data-testid="lf-lookup-close"><X size={15} /></button>
      {(l.photos || [])[0]
        ? <img src={photoUrl(l.photos[0])} alt="" className="h-24 w-24 sm:h-28 sm:w-28 rounded-lg object-cover bg-slate-100 shrink-0" />
        : <span className="h-24 w-24 rounded-lg bg-slate-100 flex items-center justify-center shrink-0"><PawPrint className="text-slate-300" /></span>}
      <div className="min-w-0 flex-1 pr-5">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-[10px] font-bold uppercase tracking-wide text-white px-1.5 py-0.5 rounded ${color}`}>{x[type]}</span>
          <LostFoundId listing={{ ...l, category: "lost-found" }} testid="lf-lookup-id" />
        </div>
        <p className="font-semibold text-[#12234A] mt-1 truncate" data-testid="lf-lookup-title">{l.title}</p>
        <p className="text-xs text-slate-500 mt-0.5">{[l.breed || l.species, l.lf_last_seen || `${l.city}, ${l.province}`].filter(Boolean).join(" · ")}</p>
        <p className="text-xs text-slate-500 mt-0.5">{l.lf_type === "lost" ? x.seen : x.posted}: {date ? new Date(date + "T12:00:00").toLocaleDateString(lang === "fr" ? "fr-CA" : "en-CA", { dateStyle: "medium" }) : "—"}
          {l.sightings > 0 && <span className="inline-flex items-center gap-1 ml-2"><Eye size={11} /> {l.sightings} {x.sightings}</span>}</p>
        <Link to={`/listing/${l.id}`} className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-[#F5551E] hover:underline" data-testid="lf-lookup-open">{x.open} <ArrowRight size={14} /></Link>
      </div>
    </div>
  );
};

export const LfIdLookup = () => {
  const { lang } = useLanguage();
  const x = TX[lang === "fr" ? "fr" : "en"];
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    if (!q.trim()) return;
    setBusy(true); setErr(""); setRes(null);
    try { const { data } = await api.get("/lost-found/lookup", { params: { q } }); setRes(data); }
    catch (e2) { setErr(errText(e2)); } finally { setBusy(false); }
  };
  return (
    <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50/70 p-4 sm:p-5 max-w-2xl" data-testid="lf-id-lookup">
      <p className="font-display font-bold text-[#12234A] flex items-center gap-2"><Hash size={16} className="text-[#F5551E]" /> {x.h}</p>
      <p className="text-xs text-slate-500 mt-0.5">{x.d}</p>
      <form onSubmit={submit} className="mt-3 flex gap-2">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={x.ph} maxLength={20}
          className="flex-1 min-w-0 h-10 rounded-xl border border-slate-200 bg-white px-3 text-sm font-mono uppercase tracking-wide focus:outline-none focus:border-[#F5551E]" data-testid="lf-lookup-input" />
        <button type="submit" disabled={busy} className="h-10 px-4 rounded-xl bg-[#12234A] hover:bg-[#1c3266] text-white text-sm font-semibold inline-flex items-center gap-1.5 transition-colors" data-testid="lf-lookup-submit">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />} {x.go}
        </button>
      </form>
      {err && <p className="mt-3 text-sm text-red-600" data-testid="lf-lookup-error">{err}</p>}
      {res && <Preview l={res} x={x} lang={lang} onClose={() => setRes(null)} />}
    </div>
  );
};
